import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * NotificationPreferencesCard — Per-user channel picker.
 *
 * Lets a user choose whether each notification bucket (moment replies,
 * payment reminders, …) reaches them via push, email, both or neither.
 *
 * Rendered on both Parent and Staff settings pages — the same buckets
 * apply, but staff typically leave email off for reply notifications.
 *
 * The component is entirely self-contained: mount `<NotificationPreferencesCard />`
 * and it does its own fetch / save.
 */
const BUCKETS = [
  {
    key: 'moment_reply',
    title: '💬 Moment replies',
    description: 'When someone (parent or staff) replies on a moment you posted or that features your child.',
  },
  {
    key: 'payment_reminder',
    title: '💰 Payment reminders',
    description: 'Gentle nudges when a booking is unpaid within 24 hours of the session.',
  },
];

export function NotificationPreferencesCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api.getNotificationPreferences();
        if (!cancelled) setPrefs(data || {});
      } catch (err) {
        if (!cancelled) toast.error(err?.message || 'Failed to load notification preferences');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (bucket, channel) => {
    const nextValue = !(prefs?.[bucket]?.[channel]);
    // Optimistic update — the toggle should feel instant. Rollback on
    // failure so the UI can't drift from the server.
    const prev = prefs;
    const next = {
      ...prefs,
      [bucket]: { ...(prefs[bucket] || {}), [channel]: nextValue },
    };
    setPrefs(next);
    setSaving(true);
    try {
      const updated = await api.updateNotificationPreferences({
        [bucket]: { [channel]: nextValue },
      });
      setPrefs(updated || next);
    } catch (err) {
      setPrefs(prev);
      toast.error(err?.message || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" data-testid="notification-preferences-card">
      <h2 style={{ marginTop: 0 }}>🔔 Notification Preferences</h2>
      <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem', marginBottom: '1rem' }}>
        Choose how you want to hear from us. In-app notifications are always on.
      </p>
      {loading ? (
        <div className="loading" data-testid="notif-prefs-loading">Loading…</div>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {BUCKETS.map((b) => {
            const bucketPrefs = prefs?.[b.key] || { push: true, email: true };
            return (
              <div
                key={b.key}
                data-testid={`notif-prefs-bucket-${b.key}`}
                style={{
                  padding: '0.75rem 0.85rem',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 'var(--radius)',
                  background: 'hsl(var(--card))',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '0.5rem',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{b.title}</div>
                  <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.2rem' }}>
                    {b.description}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <ChannelToggle
                    label="Push"
                    checked={!!bucketPrefs.push}
                    onChange={() => toggle(b.key, 'push')}
                    disabled={saving}
                    testId={`notif-prefs-${b.key}-push`}
                  />
                  <ChannelToggle
                    label="Email"
                    checked={!!bucketPrefs.email}
                    onChange={() => toggle(b.key, 'email')}
                    disabled={saving}
                    testId={`notif-prefs-${b.key}-email`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ChannelToggle({ label, checked, onChange, disabled, testId }) {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '0.85rem',
        userSelect: 'none',
        opacity: disabled ? 0.6 : 1,
      }}
      data-testid={testId}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{ margin: 0 }}
      />
      {label}
    </label>
  );
}

export default NotificationPreferencesCard;
